import { css } from 'lit';
import componentStyles from '../styles/component.styles';
export default css `
  ${componentStyles}
  /**
--------------------------------------------------
* General
--------------------------------------------------
*/
:host {
    display: none;
  }

  :host([is-open]) {
    display: block;
  }

  :host([dark]) {
    --p-text-color: var(--p-text-color-light);
    --p-bg: var(--p-bg-dark);
  }

  .file-preview {
    display: grid;
    height: 100%;
    grid-template-columns: 1fr minmax(350px, 20%);
    background: var(--p-bg);
  }

  .close-dialog {
    position: absolute;
    top: 1rem;
    left: 1rem;
    z-index: 5;
    --p-button-icon-border-radius: 50%;
  }

  .close-dialog:before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--p-bg);
    border-radius: 50%;
    z-index: -1;
    border: 1px solid var(--p-border);
  }

  @media (max-width: 1000px) {
    .file-preview {
      grid-template-columns: 1fr;
      grid-template-rows: auto 1fr;
      padding-top: 48px;
    }

    .close-dialog {
      top: 0.5rem;
      left: 0.5rem;
    }
  }

  /**
--------------------------------------------------
* Stage
--------------------------------------------------
*/
  .stage {
    background-color: var(--p-bg-light);
    position: relative;
  }

  :host([dark]) .stage {
    --p-bg-light: var(--p-bg-dark);
  }

  .stage-inner {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--p-boxed-bg);
  }

  .image {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.25s ease;
    display: none;
  }

  .view-mode-image .image,
  .view-mode-icon .image {
    display: flex;
  }

  .view-mode-icon .image img {
    max-width: 100px;
    filter: grayscale(1) invert(var(--p-dark-mode));
    opacity: 0.4;
  }

  .video {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    display: none;
  }

  .view-mode-video .video {
    display: flex;
  }

  slot[name='video']::slotted(*) {
    width: 100%;
    max-height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .stage img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    z-index: 1;
  }

  .checkered {
    position: absolute;
    z-index: 0;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml;charset=utf-8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill-opacity='.15'><path d='M50 0h50v50H50zM0 50h50v50H0z'/></svg>");
    background-size: 10px 10px;
    background-repeat: repeat;
    background-color: rgba(0, 0, 0, 0.15);
  }

  .preview-loading .image {
    visibility: hidden;
    opacity: 0;
  }

  .document-preview {
    height: 100%;
    width: 100%;
  }

  @media (max-width: 1000px) {
    .stage {
      text-align: center;
    }
    .stage-inner {
      position: static;
      border-top: 1px solid var(--p-border);
      border-bottom: 1px solid var(--p-border);
    }
    .image {
      aspect-ratio: 3/2;
      position: relative;
      min-height: 200px;
    }
    .image img {
      position: absolute;
    }

    .video {
      aspect-ratio: 16/9;
      padding: 0;
      position: relative;
      width: 100%;
    }

    .document-preview {
      max-height: 70svh;
      overflow: auto;
    }
  }

  /**
--------------------------------------------------
* Info
--------------------------------------------------
*/
  .info {
    border-left: 1px solid var(--p-border);
    display: grid;
    grid-template-rows: 1fr auto;
    background-color: var(--p-bg);
    color: var(--p-text-color);
    height: 100svh;
  }

  .file-info {
    overflow-y: auto;
    padding: 2rem 2rem 0;
    opacity: 0;
    transition: all 0.5s ease 0.1s;
  }

  .open .file-info {
    opacity: 1;
  }

  .actions {
    padding: 2rem;
  }
  .actions-inner {
    opacity: 0;
    transition: all 0.5s ease 0.1s;
  }

  .open .actions-inner {
    opacity: 1;
  }

  .embed-code-button {
    margin-bottom: 1rem;
  }

  .file-loading .file-info {
    visibility: hidden;
    opacity: 0;
  }

  .info h2 {
    font-family: var(--p-h4-font);
    font-size: var(--p-h4-font-size);
    font-weight: var(--p-h4-font-weight);
    font-style: var(--p-h4-font-style);
    line-height: var(--p-h4-line-height);
    text-transform: var(--p-h4-transform);
    letter-spacing: var(--p-h4-letter-spacing);
    margin: 0 0 var(--p-h4-margin-bottom);
    color: var(--p-text-color);
    word-break: break-word;
  }

  .info p.description {
    font-size: 1rem;
    line-height: var(--p-body-line-height);
    font-style: var(--p-body-font-style);
    font-weight: var(--p-body-font-weight);
    letter-spacing: var(--p-body-letter-spacing);
    margin: 0 0 1rem;
  }

  dl {
    margin: 0;
    line-height: 1.4;
    padding: 0;
    font-size: 0.875rem;
    color: var(--p-text-color);
  }

  dt {
    font-size: var(--p-font-size-small);
    opacity: var(--p-text-meta-opacity);
    word-break: break-word;
  }

  dd {
    margin: 0 0 0.8em;
    word-break: break-word;
  }

  dd:last-child {
    margin-bottom: 0;
  }

  .file-info .rights-info {
    margin-bottom: 0.5em;
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .mark-label {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  @media (max-width: 1000px) {
    .info {
      border-left: none;
      height: auto;
      overflow-y: unset;
    }

    .file-info {
      padding: 1rem 1rem 0;
    }
    .actions {
      padding: 1rem;
    }
  }

  /**
--------------------------------------------------
* Stepper
--------------------------------------------------
*/
  .stepper {
    position: absolute;
    bottom: 2rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    background: var(--p-bg);
    border: 1px solid var(--p-border);
    border-radius: 20px;
    font-size: 0.875rem;
    align-items: center;
    z-index: 3;
  }

  @media (max-width: 1000px) {
    .stepper {
      position: relative;
      transform: none;
      left: unset;
      top: 0;
      display: flex;
      border: none;
      border-bottom: 1px solid var(--p-border);
      border-radius: 0;
    }

    .counter {
      flex-grow: 1;
    }

    .stepper button {
      border-radius: 0 !important;
    }
  }

  .counter {
    padding-inline: 0.5rem;
    white-space: nowrap;
    color: var(--p-text-color);
  }

  .stepper button {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    color: var(--p-text-color);
  }

  .stepper button:hover {
    background: var(--p-text-color-contrast-100);
  }

  .stepper button:active {
    background: var(--p-bg);
  }

  .stepper button.prev {
    border-right: 1px solid var(--p-border);
    border-radius: 20px 0 0 20px;
  }

  .stepper button.next {
    border-left: 1px solid var(--p-border);
    border-radius: 0 20px 20px 0;
  }

  .stepper button:disabled {
    background: var(--p-text-color-contrast-100);
    color: var(--p-text-color-contrast-200);
    cursor: not-allowed;
  }

  /**
--------------------------------------------------
* Loader
--------------------------------------------------
*/
  .loader {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    position: absolute;
    animation: rotate 0.6s linear infinite;
    top: 50%;
    left: 50%;
    margin-left: -9px;
    margin-top: -9px;
    opacity: 0;
    visibility: hidden;
    transition: all 0.25s ease;
    transition-delay: 0s;
  }
  .loader::before {
    content: '';
    box-sizing: border-box;
    position: absolute;
    inset: 0px;
    border-radius: 50%;
    border: 2px solid var(--p-text-color);
    animation: prixClipFix 1.2s linear infinite;
  }

  .preview-loading .loader {
    visibility: visible;
    opacity: 1;
    transition-delay: 0.25s;
  }

  @keyframes rotate {
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes prixClipFix {
    0% {
      clip-path: polygon(50% 50%, 0 0, 0 0, 0 0, 0 0, 0 0);
    }
    25% {
      clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 0, 100% 0, 100% 0);
    }
    50% {
      clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 100%, 100% 100%, 100% 100%);
    }
    75% {
      clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 100%, 0 100%, 0 100%);
    }
    100% {
      clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 100%, 0 100%, 0 0);
    }
  }
`;
